import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Umbrella, Clock, CheckCircle2, XCircle, AlertCircle,
  ChevronRight, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_STYLES = {
  PENDING: "bg-amber-100 text-amber-700 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
  CANCELLED: "bg-slate-100 text-slate-500 border-slate-200",
  DRAFT: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function LMSMyLeave() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => { fetchApps(); }, [statusFilter, page]);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await axios.get(`${API}/lms/my-leaves`, {
        params, headers: getHeaders(), withCredentials: true,
      });
      setApplications(res.data.applications || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  };

  const handleCancel = async (appId) => {
    try {
      await axios.post(`${API}/lms/cancel/${appId}`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Application cancelled");
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">My Leave Applications</h2>
        <Button onClick={() => navigate("/hr-hub/leave-management/request")}
          className="h-8 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500">
          + New Application
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 text-xs rounded-none w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all" className="text-xs">All Status</SelectItem>
            <SelectItem value="PENDING" className="text-xs">Pending</SelectItem>
            <SelectItem value="APPROVED" className="text-xs">Approved</SelectItem>
            <SelectItem value="REJECTED" className="text-xs">Rejected</SelectItem>
            <SelectItem value="CANCELLED" className="text-xs">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] text-slate-400">{total} total</span>
      </div>

      {/* List */}
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto py-8" />
      ) : applications.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm bg-white border border-slate-200">
          <Umbrella className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p>No leave applications found</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {applications.map((app) => (
            <div key={app.id} className="px-4 py-3 hover:bg-app">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800">{app.application_number}</span>
                    <Badge className={`text-[9px] px-1.5 py-0 border-none ${STATUS_STYLES[app.status] || STATUS_STYLES.PENDING}`}>
                      {app.status}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium text-slate-700 mt-0.5">{app.leave_type_name}</p>
                  <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-0.5">
                    <span className="flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {app.start_date} to {app.end_date}
                    </span>
                    <span>{app.total_days} day{app.total_days !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(app.status === "PENDING" || app.status === "DRAFT") && (
                    <Button variant="ghost" size="sm" onClick={() => handleCancel(app.id)}
                      className="h-6 text-[10px] text-red-500">
                      Cancel
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/hr-hub/leave-management/my-leave`)}
                    className="h-6 w-6 p-0">
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="h-7 text-xs rounded-none">Previous</Button>
          <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}
            className="h-7 text-xs rounded-none">Next</Button>
        </div>
      )}
    </div>
  );
}
